import { useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AdminPage } from "@/components/admin/AdminSidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import type { Tables, TablesInsert } from "@/integrations/supabase/types";
import { useDataScope } from "@/hooks/useDataScope";
import { toast } from "sonner";
import { BookOpen, Download, Eye, Pencil, Plus, Trash2 } from "lucide-react";
import { PdfViewer } from "@/components/admin/PdfViewer";
import {
  BC_BUCKET,
  prepareKnowledgeInsert,
  documentFilename,
  validateDocumentFiles,
  type DocumentFiles,
} from "@/lib/base-conocimiento";

export const Route = createFileRoute("/admin/base-conocimiento")({
  head: () => ({
    meta: [
      { title: "Base de conocimientos — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: BaseConocimientoPage,
});
type Doc = Tables<"base_conocimiento_docs">;
type Variant = DocumentFiles & { key: string; titulo: string };
const blankVariant = (): Variant => ({
  key: crypto.randomUUID(),
  titulo: "",
  pdf: null,
  word: null,
});

function BaseConocimientoPage() {
  const qc = useQueryClient();
  const { isStaff, memberships, agenciaVista } = useDataScope();
  const [openedId, setOpenedId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ id: string; url: string } | null>(null);
  const previewRequest = useRef(0);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Doc | null>(null);
  const [parent, setParent] = useState<Doc | null>(null);
  const [titulo, setTitulo] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [orden, setOrden] = useState("0");
  const [agenciaId, setAgenciaId] = useState("");
  const [hasVariants, setHasVariants] = useState(false);
  const [files, setFiles] = useState<DocumentFiles>({ pdf: null, word: null });
  const [variants, setVariants] = useState<Variant[]>([]);
  const agencias = useQuery({
    queryKey: ["bc-agencias-staff"],
    enabled: isStaff,
    staleTime: 300_000,
    queryFn: async () => {
      const { data, error } = await supabase.from("agencias").select("id, nombre").order("nombre");
      if (error) throw error;
      return data ?? [];
    },
  });
  const options = useMemo(
    () =>
      isStaff
        ? (agencias.data ?? [])
        : memberships.map((m) => ({ id: m.agencia.id, nombre: m.agencia.nombre })),
    [isStaff, agencias.data, memberships],
  );
  const agencyFilter =
    isStaff && agenciaVista && agenciaVista !== "sin_agencia" ? agenciaVista : null;
  const q = useQuery({
    queryKey: ["base-conocimiento", { agencyFilter }],
    staleTime: 60_000,
    queryFn: async () => {
      let query = supabase
        .from("base_conocimiento_docs")
        .select("*")
        .order("orden")
        .order("titulo");
      if (agencyFilter) query = query.eq("agencia_id", agencyFilter);
      const { data, error } = await query;
      if (error) throw error;
      return data ?? [];
    },
  });
  const docs = q.data ?? [];
  const opened = docs.find((d) => d.id === openedId);
  const selected = docs.find((d) => d.id === selectedId);
  const children = (id: string) => docs.filter((d) => d.principal_id === id);

  function openForm(doc: Doc | null = null, principal: Doc | null = null) {
    setEditing(doc);
    setParent(principal);
    setTitulo(doc?.titulo ?? "");
    setDescripcion(doc?.descripcion ?? "");
    setOrden(String(doc?.orden ?? 0));
    setAgenciaId(doc?.agencia_id ?? principal?.agencia_id ?? agencyFilter ?? options[0]?.id ?? "");
    setHasVariants(doc?.tiene_variantes ?? false);
    setFiles({ pdf: null, word: null });
    setVariants([]);
    setFormOpen(true);
  }
  async function cleanup(paths: string[]) {
    if (!paths.length) return;
    try {
      const { error } = await supabase.storage.from(BC_BUCKET).remove(paths);
      if (error) throw error;
    } catch {
      toast.warning("No se pudieron eliminar algunos archivos del almacenamiento.");
    }
  }
  const save = useMutation({
    mutationFn: async () => {
      if (!agenciaId || !titulo.trim())
        throw new Error("Selecciona una agencia e indica un título.");
      if (!Number.isSafeInteger(Number(orden)))
        throw new Error("El orden debe ser un número entero.");
      if (!hasVariants) validateDocumentFiles(files, editing ?? undefined);
      if (hasVariants && !editing && variants.length === 0)
        throw new Error("Añade al menos una variante.");
      if (hasVariants)
        for (const v of variants) {
          if (!v.titulo.trim()) throw new Error("Indica el nombre de cada variante.");
          validateDocumentFiles(v);
        }
      const uploaded: string[] = [];
      const replaced: string[] = [];
      const upload = async (file: File | null, oldPath: string | null) => {
        if (!file) return oldPath;
        const ext = file.name.split(".").pop()!.toLowerCase();
        const path = `${agenciaId}/${crypto.randomUUID()}.${ext}`;
        const contentType =
          ext === "pdf"
            ? "application/pdf"
            : ext === "doc"
              ? "application/msword"
              : "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
        const { error } = await supabase.storage
          .from(BC_BUCKET)
          .upload(path, file, { contentType, upsert: false });
        if (error) throw error;
        uploaded.push(path);
        if (oldPath) replaced.push(oldPath);
        return path;
      };
      try {
        const id = editing?.id ?? crypto.randomUUID();
        const pdfPath = await upload(hasVariants ? null : files.pdf, editing?.storage_path ?? null);
        const wordPath = await upload(hasVariants ? null : files.word, editing?.word_path ?? null);
        const row = {
          agencia_id: agenciaId,
          titulo: titulo.trim(),
          descripcion: descripcion.trim() || null,
          orden: Number(orden),
          tiene_variantes: hasVariants,
          principal_id: editing?.principal_id ?? parent?.id ?? null,
          pdf_url: pdfPath ?? editing?.pdf_url ?? null,
          storage_path: pdfPath,
          word_path: wordPath,
        };
        if (editing) {
          const { data, error } = await supabase
            .from("base_conocimiento_docs")
            .update(row)
            .eq("id", id)
            .select("id")
            .single();
          if (error || !data) throw error ?? new Error("No se pudo actualizar el documento.");
        } else {
          const { data: auth, error: authError } = await supabase.auth.getUser();
          if (authError || !auth.user) throw authError ?? new Error("Inicia sesión de nuevo.");
          const rows: TablesInsert<"base_conocimiento_docs">[] = [
            { ...row, id, created_by: auth.user.id },
          ];
          if (hasVariants)
            for (const [index, v] of variants.entries()) {
              const pdf = await upload(v.pdf, null);
              const word = await upload(v.word, null);
              rows.push({
                agencia_id: agenciaId,
                principal_id: id,
                titulo: v.titulo.trim(),
                pdf_url: pdf,
                storage_path: pdf,
                word_path: word,
                orden: index,
                created_by: auth.user.id,
              });
            }
          // One INSERT keeps the parent and all its variants atomic.
          const { error } = await supabase
            .from("base_conocimiento_docs")
            .insert(prepareKnowledgeInsert(rows));
          if (error) throw error;
        }
      } catch (error) {
        await cleanup(uploaded);
        throw error;
      }
      await cleanup(replaced);
    },
    onSuccess: () => {
      toast.success(editing ? "Documento actualizado" : "Documento añadido");
      setFormOpen(false);
      ++previewRequest.current;
      setLoadingPreview(false);
      setPreview(null);
      setSelectedId(null);
      void qc.invalidateQueries({ queryKey: ["base-conocimiento"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const remove = useMutation({
    mutationFn: async (doc: Doc) => {
      // Return all affected paths, including cascaded children, before deleting.
      const { data: family, error: readError } = await supabase
        .from("base_conocimiento_docs")
        .select("storage_path, word_path")
        .or(`id.eq.${doc.id},principal_id.eq.${doc.id}`);
      if (readError) throw readError;
      const { error } = await supabase
        .from("base_conocimiento_docs")
        .delete()
        .eq("id", doc.id)
        .select("id")
        .single();
      if (error) throw error;
      await cleanup(
        (family ?? []).flatMap((d) =>
          [d.storage_path, d.word_path].filter((p): p is string => !!p),
        ),
      );
    },
    onSuccess: () => {
      toast.success("Documento eliminado");
      ++previewRequest.current;
      setLoadingPreview(false);
      setSelectedId(null);
      setPreview(null);
      void qc.invalidateQueries({ queryKey: ["base-conocimiento"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
  async function showPdf(doc: Doc) {
    const request = ++previewRequest.current;
    setSelectedId(doc.id);
    setPreview(null);
    setLoadingPreview(true);
    try {
      let url = doc.pdf_url;
      if (doc.storage_path) {
        const { data, error } = await supabase.storage
          .from(BC_BUCKET)
          .createSignedUrl(doc.storage_path, 1800);
        if (error) throw error;
        url = data.signedUrl;
      }
      if (!url) throw new Error("Este documento no tiene PDF.");
      if (request === previewRequest.current) setPreview({ id: doc.id, url });
    } catch (e) {
      if (request === previewRequest.current)
        toast.error(e instanceof Error ? e.message : "No se pudo abrir el PDF.");
    } finally {
      if (request === previewRequest.current) setLoadingPreview(false);
    }
  }
  async function download(doc: Doc, format: "pdf" | "word") {
    setDownloading(true);
    try {
      const path = format === "pdf" ? doc.storage_path : doc.word_path;
      let blob: Blob;
      if (path) {
        const { data, error } = await supabase.storage.from(BC_BUCKET).download(path);
        if (error) throw error;
        blob = data;
      } else if (format === "pdf" && doc.pdf_url) {
        const response = await fetch(doc.pdf_url);
        if (!response.ok) throw new Error("No se pudo descargar el PDF.");
        blob = await response.blob();
      } else throw new Error("Archivo no disponible.");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = documentFilename(doc.titulo, path ?? "documento.pdf");
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo descargar el archivo.");
    } finally {
      setDownloading(false);
    }
  }
  function documentActions(doc: Doc) {
    return (
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={() => openForm(doc)}>
          <Pencil className="mr-1 h-4 w-4" />
          Editar
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={remove.isPending}
          onClick={() => {
            if (
              confirm(
                doc.tiene_variantes
                  ? "¿Eliminar este documento y todas sus variantes?"
                  : "¿Eliminar este documento?",
              )
            )
              remove.mutate(doc);
          }}
        >
          <Trash2 className="mr-1 h-4 w-4" />
          Eliminar
        </Button>
      </div>
    );
  }
  function fileInputs(
    value: DocumentFiles,
    change: (v: DocumentFiles) => void,
    prefix: string,
    existing?: Doc | null,
  ) {
    return (
      <div className="grid gap-3 sm:grid-cols-2">
        {(["pdf", "word"] as const).map((format) => (
          <div key={format} className="space-y-1.5">
            <Label htmlFor={`${prefix}-${format}`}>
              {format === "pdf" ? "Archivo PDF" : "Archivo Word (.doc o .docx)"}
              {existing && (format === "pdf" ? existing.pdf_url : existing.word_path)
                ? " · disponible"
                : " *"}
            </Label>
            <Input
              id={`${prefix}-${format}`}
              type="file"
              accept={
                format === "pdf"
                  ? ".pdf,application/pdf"
                  : ".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              }
              onChange={(e) => change({ ...value, [format]: e.target.files?.[0] ?? null })}
            />
          </div>
        ))}
      </div>
    );
  }
  return (
    <AdminPage
      title="Base de conocimientos"
      description="Documentación interna de tu agencia. Elige una variante, consulta el PDF o descarga el Word para editarlo."
      actions={
        options.length ? (
          <Button onClick={() => openForm()}>
            <Plus className="mr-2 h-4 w-4" />
            Añadir documento
          </Button>
        ) : undefined
      }
    >
      {q.isLoading && <p role="status">Cargando documentos…</p>}
      {q.error && (
        <p className="text-destructive">No se pudieron cargar los documentos: {q.error.message}</p>
      )}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {docs
          .filter((d) => !d.principal_id)
          .map((doc) => (
            <Card key={doc.id}>
              <CardHeader>
                <CardTitle className="flex gap-2 text-base">
                  <BookOpen className="h-5 w-5 shrink-0" />
                  {doc.titulo}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {doc.descripcion && (
                  <p className="text-sm text-muted-foreground">{doc.descripcion}</p>
                )}
                {doc.tiene_variantes && (
                  <p className="text-sm">{children(doc.id).length} variantes</p>
                )}
                {!doc.tiene_variantes && !doc.word_path && (
                  <p className="text-xs text-muted-foreground">Word pendiente de adjuntar</p>
                )}
                <Button
                  variant="outline"
                  onClick={() => {
                    setOpenedId(doc.id);
                    setSelectedId(null);
                    setPreview(null);
                    if (!doc.tiene_variantes) void showPdf(doc);
                  }}
                >
                  <Eye className="mr-2 h-4 w-4" />
                  {doc.tiene_variantes ? "Elegir variante" : "Ver documento"}
                </Button>
                {documentActions(doc)}
              </CardContent>
            </Card>
          ))}
      </div>
      {!q.isLoading && !q.error && !docs.length && (
        <p>Todavía no hay documentos en la base de conocimientos.</p>
      )}
      <Dialog
        open={!!opened}
        onOpenChange={(v) => {
          if (!v) {
            ++previewRequest.current;
            setOpenedId(null);
            setSelectedId(null);
            setPreview(null);
            setLoadingPreview(false);
          }
        }}
      >
        <DialogContent className="max-h-[90vh] max-w-5xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{opened?.titulo}</DialogTitle>
          </DialogHeader>
          {opened?.tiene_variantes && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p>Selecciona la variante que necesitas.</p>
                <Button size="sm" onClick={() => openForm(null, opened)}>
                  <Plus className="mr-1 h-4 w-4" />
                  Añadir variante
                </Button>
              </div>
              {!children(opened.id).length && (
                <p className="text-sm text-muted-foreground">
                  Todavía no hay variantes. Añade la primera.
                </p>
              )}
              {[...(opened.pdf_url ? [opened] : []), ...children(opened.id)].map((doc) => (
                <div
                  key={doc.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-3"
                >
                  <Button
                    variant={selectedId === doc.id ? "secondary" : "ghost"}
                    className="h-auto whitespace-normal text-left"
                    onClick={() => void showPdf(doc)}
                  >
                    {doc.id === opened.id ? "Documento principal (original)" : doc.titulo}
                  </Button>
                  {doc.id !== opened.id && documentActions(doc)}
                </div>
              ))}
            </div>
          )}
          {selected && (
            <div className="space-y-2">
              <p className="font-medium">{selected.titulo}</p>
              <div className="flex flex-wrap gap-2">
                <Button
                  disabled={downloading || !selected.pdf_url}
                  onClick={() => void download(selected, "pdf")}
                >
                  <Download className="mr-2 h-4 w-4" />
                  Descargar PDF
                </Button>
                <Button
                  variant="outline"
                  disabled={downloading || !selected.word_path}
                  onClick={() => void download(selected, "word")}
                >
                  <Download className="mr-2 h-4 w-4" />
                  Descargar Word
                </Button>
              </div>
              {!selected.word_path && (
                <p className="text-sm text-muted-foreground">
                  Word pendiente de adjuntar. Puedes añadirlo al editar el documento.
                </p>
              )}
            </div>
          )}
          {loadingPreview && <p role="status">Cargando PDF…</p>}
          {preview && <PdfViewer key={preview.url} url={preview.url} title={selected?.titulo} />}
        </DialogContent>
      </Dialog>
      <Dialog
        open={formOpen}
        onOpenChange={(v) => {
          if (!save.isPending) setFormOpen(v);
        }}
      >
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editing
                ? "Editar documento"
                : parent
                  ? `Añadir variante · ${parent.titulo}`
                  : "Añadir documento"}
            </DialogTitle>
          </DialogHeader>
          <fieldset disabled={save.isPending} className="space-y-4">
            {options.length > 1 && (
              <div className="space-y-1.5">
                <Label>Agencia</Label>
                <Select
                  value={agenciaId}
                  onValueChange={setAgenciaId}
                  disabled={!!editing || !!parent}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona una agencia" />
                  </SelectTrigger>
                  <SelectContent>
                    {options.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="bc-title">Título *</Label>
              <Input id="bc-title" value={titulo} onChange={(e) => setTitulo(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bc-description">Descripción</Label>
              <Textarea
                id="bc-description"
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bc-order">Orden</Label>
              <Input
                id="bc-order"
                type="number"
                step="1"
                value={orden}
                onChange={(e) => setOrden(e.target.value)}
              />
            </div>
            {!parent && !editing?.principal_id && (
              <Label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={hasVariants}
                  disabled={!!editing?.tiene_variantes}
                  onChange={(e) => {
                    setHasVariants(e.target.checked);
                    if (e.target.checked && !editing && !variants.length)
                      setVariants([blankVariant()]);
                  }}
                />
                Este documento tiene varias variantes
              </Label>
            )}
            {!hasVariants && fileInputs(files, setFiles, "bc", editing)}
            {hasVariants && editing && (
              <p className="text-sm text-muted-foreground">
                Gestiona las variantes abriendo el documento principal. Sus archivos originales se
                conservan.
              </p>
            )}
            {hasVariants && !editing && (
              <div className="space-y-4">
                {variants.map((v, i) => (
                  <div key={v.key} className="space-y-3 rounded-md border p-3">
                    <div className="flex items-end gap-2">
                      <div className="flex-1 space-y-1.5">
                        <Label htmlFor={v.key}>Nombre de la variante {i + 1} *</Label>
                        <Input
                          id={v.key}
                          placeholder="Traspaso · Con exclusividad"
                          value={v.titulo}
                          onChange={(e) =>
                            setVariants((all) =>
                              all.map((item) =>
                                item.key === v.key ? { ...item, titulo: e.target.value } : item,
                              ),
                            )
                          }
                        />
                      </div>
                      <Button
                        variant="outline"
                        aria-label={`Quitar variante ${i + 1}`}
                        onClick={() =>
                          setVariants((all) => all.filter((item) => item.key !== v.key))
                        }
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    {fileInputs(
                      v,
                      (value) =>
                        setVariants((all) =>
                          all.map((item) => (item.key === v.key ? { ...item, ...value } : item)),
                        ),
                      v.key,
                    )}
                  </div>
                ))}
                <Button
                  variant="outline"
                  onClick={() => setVariants((all) => [...all, blankVariant()])}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Añadir variante
                </Button>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Adjunta PDF y Word para cada documento o variante. Máximo 20 MB por archivo. Al
              editar, los archivos no sustituidos se conservan.
            </p>
          </fieldset>
          <DialogFooter>
            <Button variant="outline" disabled={save.isPending} onClick={() => setFormOpen(false)}>
              Cancelar
            </Button>
            <Button disabled={save.isPending} onClick={() => save.mutate()}>
              {save.isPending ? "Guardando…" : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminPage>
  );
}
